import React, { useEffect, useState, useCallback } from 'react';
import { auditAPI } from '../api';
import toast from 'react-hot-toast';
import { Shield, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const ACTION_META = {
  CREATE:         { label: 'Oluşturma',    color: 'bg-emerald-100 text-emerald-700' },
  UPDATE:         { label: 'Güncelleme',   color: 'bg-blue-100    text-blue-700'    },
  DELETE:         { label: 'Silme',        color: 'bg-red-100     text-red-600'     },
  BULK_DELETE:    { label: 'Toplu Silme',  color: 'bg-red-100     text-red-600'     },
  IMPORT:         { label: 'İçe Aktarım', color: 'bg-purple-100  text-purple-700'  },
  LOGIN:          { label: 'Giriş',        color: 'bg-navy-100    text-navy-700'    },
  PASSWORD_CHANGE:{ label: 'Şifre Değişimi', color: 'bg-amber-100 text-amber-700'  },
};

const ENTITY_LABELS = {
  firma: 'Firma', kurum: 'Kurum', ilce_osb: 'İlçe/OSB',
  ayar: 'Ayar', user: 'Kullanıcı',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await auditAPI.list({
        page,
        page_size: PAGE_SIZE,
        action: actionFilter || undefined,
        entity_type: entityFilter || undefined,
      });
      setLogs(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Log kayıtları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display text-navy-800">Audit Log</h1>
          <p className="text-navy-400 text-sm mt-0.5">
            Tüm sistem işlemleri — toplam <strong>{total.toLocaleString('tr-TR')}</strong> kayıt
          </p>
        </div>
        <button className="btn-secondary" onClick={load}>
          <RefreshCw size={15} /> Yenile
        </button>
      </div>

      {/* Filtreler */}
      <div className="card p-4 flex flex-wrap gap-3">
        <select
          className="select w-auto min-w-[160px]"
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
        >
          <option value="">Tüm İşlemler</option>
          {Object.entries(ACTION_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select
          className="select w-auto min-w-[160px]"
          value={entityFilter}
          onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
        >
          <option value="">Tüm Kaynaklar</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Tablo */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-navy-200 border-t-navy-600 rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-navy-400">
            <Shield size={40} className="mb-3 text-navy-200" />
            <p className="text-sm">Henüz log kaydı yok</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th">Tarih / Saat</th>
                  <th className="th">İşlem</th>
                  <th className="th">Kaynak</th>
                  <th className="th">Kayıt Adı</th>
                  <th className="th">Kullanıcı</th>
                  <th className="th">IP</th>
                  <th className="th">Detay</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const meta = ACTION_META[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-700' };
                  return (
                    <tr key={log.id} className="table-row">
                      <td className="td whitespace-nowrap">
                        <span className="font-mono text-xs text-navy-500">
                          {format(new Date(log.created_at), 'dd MMM yyyy', { locale: tr })}
                        </span>
                        <br />
                        <span className="font-mono text-xs text-navy-400">
                          {format(new Date(log.created_at), 'HH:mm:ss')}
                        </span>
                      </td>
                      <td className="td">
                        <span className={`badge ${meta.color}`}>{meta.label}</span>
                      </td>
                      <td className="td text-xs text-navy-500">
                        {ENTITY_LABELS[log.entity_type] || log.entity_type || '—'}
                      </td>
                      <td className="td">
                        <span className="text-navy-700 text-sm font-medium truncate block max-w-[200px]">
                          {log.entity_name || '—'}
                        </span>
                        {log.entity_id && (
                          <span className="text-navy-400 text-xs font-mono">#{log.entity_id}</span>
                        )}
                      </td>
                      <td className="td text-xs text-navy-500">{log.user_email || 'sistem'}</td>
                      <td className="td">
                        <span className="font-mono text-xs text-navy-400">{log.ip_address || '—'}</span>
                      </td>
                      <td className="td">
                        {log.details && (
                          <details className="group">
                            <summary className="text-xs text-navy-400 cursor-pointer hover:text-navy-600 select-none">
                              Göster
                            </summary>
                            <pre className="mt-1 text-xs bg-navy-50 rounded-lg p-2 max-w-xs overflow-auto max-h-28 text-navy-700">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-navy-100">
            <span className="text-xs text-navy-400">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} / {total}
            </span>
            <div className="flex gap-1">
              <button
                className="btn-ghost py-1 px-2"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 1}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm text-navy-600">
                {page} / {totalPages}
              </span>
              <button
                className="btn-ghost py-1 px-2"
                onClick={() => setPage((p) => p + 1)}
                disabled={page === totalPages}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
